import events from 'Store/events'
import SIPUAClass from '@/sipua/SIPUA'
import Sounds from '@/sipua/sounds'
import Session from 'Store/class/rtp_session'
import LocalStorage from 'Store/class/local_storage'
import BrowserMedia from 'Store/sipua/browser_media'
import { NotifyClear } from 'Store/notify'
import * as RMQClient from '@/rmqclient'
import { SIPRequestPAI } from 'Store/class/helper'
import debug from 'debug'

const dbg = debug('store:action:SIPUA:')

const SIPUA = new SIPUAClass()
const snd = new Sounds()
const DEFAULT_MAX_CALLS = 4 // max concurrent calls limit.

const actions = {
  call ({ commit, dispatch, getters }, num) {
    if (getters.maxCallsHit) {
      events.pubError({
        key: 'sipua.error.maxCallsOut',
        args: { limit: getters.maxCalls }
      })
      return
    }
    commit('SET_CALLING', true)
    dispatch('callsLogDialed', num, { root: true })
    dbg(`Calling number ${num}`)
    if (!SIPUA.call(num)) {
      commit('SET_CALLING', false)
      snd.stopCallProgress()
    }
  },

  callAttendedXfer ({ commit, dispatch }, { num, cid, user }) {
    // ignore max calls limit
    commit('SET_CALLING', true)
    dispatch('callsLogDialed', num, { root: true })
    commit('CALL_HOLD', { callid: cid, enabled: true })
    SIPUA.call(num, {
      eventHandlers: {
        sending: (e) =>
          dispatch('callSending', {
            event: e,
            opt: { xferClid: cid, xferUser: user }
          }),
        ended: (e) => commit('CALL_ENDED', e)
      }
    })
  },

  callInit ({ state, commit, rootGetters }, data) {
    commit('CALL_INIT', data)
    const autoAnswerSettings = rootGetters['settings/intercom']?.callpanel
    const autoAnswer = data.recvCall && data.autoAnsw && autoAnswerSettings
    if (!autoAnswer) {
      return
    }
    dbg(
      'intercom option is enabled and SIP header Call-Info is present. Auto-answer this call'
    )
    if (state.sessions.lenght > 0) {
      dbg('skip auto-answer if there are running calls')
      return
    }
    // auto-answer here
    SIPUA.callAccept(data.callid)
  },

  callSending ({ commit }, { event, opt }) {
    const req = event?.request
    if (!req) {
      events.pubError('Failed to send call! Check debug.')
      dbg('ERROR: Invalid event in callSending: %o', event)
      return
    }
    const data = {
      callid: req.call_id,
      name: req.to.display_name,
      number: req.to.uri.user,
      outgoing: true
    }
    if (opt?.xferClid) {
      data.xfer = { callid: opt.xferClid, number: opt.xferUser }
    }
    commit('CALL_INIT', data)
  },

  /**
   * call and compose DTMF when answered
   * useful for calls like to conferences with pin
   */
  callConfBridge ({ commit }, { num, pin }) {
    const opts = {}
    commit('SET_CALLING', true)

    if (pin) {
      opts.extraHeaders = [`X-Conf-PIN: ${pin}`]
    }

    SIPUA.call(num, opts)
  },

  chanspy ({ commit, dispatch }, { num, chan, mode, legnum }) {
    commit('SET_CALLING', true)
    const number = `***${num}`
    const extraHeaders = [
      `X-Callspy-Chan: ${chan || ''}`,
      `X-Callspy-Mode: ${mode || ''}`,
      `X-Callspy-Legnum: ${legnum || ''}`
    ]
    let callid = ''
    // any barge in mode must be started mutted
    const eventHandlers = {
      sending: (event) => {
        dispatch('callSending', { event }).then(() => {
          callid = event.request?.call_id
          commit('CALL_SET_BARGEIN', { callid, num, legnum, mode })
        })
      },
      confirmed: (e) => {
        // temporary solution to set connected chanspy session mutted
        // on hook up. Doing mute right away, without some delay is
        // causing "fake" mute when user see that the call is mutted
        // but actually audio from spyer user is sent.
        // The delay with 0.5 sec works fine for now and does not
        // change user experience.
        // TODO: barge in mute on answer should be done without timeout
        new Promise((resolve, reject) => {
          setTimeout(() => resolve(), 1000)
        }).then(() => {
          commit('CALL_MUTE', { callid, enabled: true })
          dbg(`set chanspy call mutted when connected: '${callid}'`)
        })
      },
      ended: (event) => commit('CALL_ENDED', event)
    }
    SIPUA.call(number, { extraHeaders, eventHandlers })
  },

  parked ({ commit }, data) {
    if (data?.event?.toUpperCase() !== 'UNPARKEDCALL') return

    const cid = data?.retrieverchanvariable?.SIPCALLID
    if (!cid) return

    const name = data.parkeecalleridname
    const num = data.parkeecalleridnum

    commit('UPDATE_PARK_PICKUP_CALL', { cid, name, num })
  },

  setStatus ({ commit }, status) {
    commit('SET_STATUS', status)
  },

  setInputDevice ({ commit }, dev) {
    commit('SET_INPUT_DEVICE', dev)
  },

  setOutputDevice ({ commit }, dev) {
    commit('SET_OUTPUT_DEVICE', dev)
  },

  setRingerDevice ({ commit }, dev) {
    commit('SET_RINGER_DEVICE', dev)
  },

  setRingerDevEnabled ({ commit }, enabled) {
    commit('SET_RINGDEV_ENABLED', enabled)
  },

  setSIPUAPhoneDisplay: {
    root: true,
    handler ({ commit }, display) {
      commit('SET_PHONE_DISPLAY', display)
    }
  },

  sipuaUnregister: {
    root: true,
    handler () {
      SIPUA.unregister()
    }
  },

  sipuaDestroy: {
    root: true,
    handler () {
      SIPUA.destroy()
    }
  },

  setSIPUADialNumber: {
    root: true,
    handler ({ commit }, dialNumber) {
      commit('SET_PHONE_DIAL_NUMBER', dialNumber)
    }
  },

  setSIPUARealmID: {
    root: true,
    handler ({ commit }, realmId) {
      commit('SET_REALM_ID', realmId)
    }
  },

  setAudioVolume: function ({ commit }, val) {
    commit('SET_AUDIO_VOLUME', val)
  },

  acceptCall: function ({ state, dispatch }, callid) {
    SIPUA.callAccept(callid)
    const sess = state.sessions.find((s) => s.callid === callid)
    if (sess) {
      dispatch(
        'callsLogRecevd',
        { num: sess.number, name: sess.name },
        {
          root: true
        }
      )
    }
  },

  hangupCall: function ({ commit }, callid) {
    commit('SESSION_DELETE', callid)
    NotifyClear(callid)
  },

  hangupAllCalls: function ({ commit, state }) {
    for (let i = 0; i < state.sessions.length; i++) {
      const callid = state.sessions[i].callid
      commit('SESSION_DELETE', callid)
    }
  },

  xferCall: function (_, { callid, number }) {
    dbg(`Transfer call ${callid} to ${number}`)
    if (!SIPUAClass.isValidNum(number)) {
      events.pubError(`Invalid or empty destination: '${number}'`)
      return
    }
    SIPUA.callXFer(callid, number)
  },

  parkCall: function ({ state }, callid) {
    dbg(`Parking call to exten '${state.parkExten}', call-id: ${callid}`)
    SIPUA.callXFer(callid, state.parkExten)
  },

  xferToConfr: function (_, { callid, conference }) {
    const opts = {}

    if (conference.pin) {
      opts.extraHeaders = [`X-Conf-PIN: ${conference.pin}`]
    }
    let exten = conference.exten
    if (conference.personal) {
      exten = `*99${exten}`
    }
    dbg(
      `Transfer call ${callid} to conference ${exten} with pin '${conference.pin}'`
    )

    SIPUA.callXFer(callid, exten, null, opts)
  },

  confirmAttendedXfer (_, { number, callid, replace }) {
    dbg(
      `Attended transfer call ${callid} to ${number} replace callid ${replace}`
    )
    if (!SIPUAClass.isValidNum(number)) {
      events.pubError(
        `Invalid or empty destination for attended xfer: '${number}'`
      )
      return
    }
    SIPUA.callXFer(callid, number, replace)
  },

  sendDTMF: function (_, { callid, dtmf }) {
    dbg(`Send DTMF "${dtmf}" to call ${callid}`)
    snd.DTMF(dtmf)
    SIPUA.sendDTMF(callid, dtmf)
  },

  holdCall: function ({ commit }, { callid, enabled }) {
    commit('CALL_HOLD', { callid, enabled })
  },

  muteCall: function ({ commit }, { callid, enabled }) {
    commit('CALL_MUTE', { callid, enabled })
  },

  publishCallFwd: function ({ rootGetters }, enabled) {
    const fc = rootGetters['settings/featureCodes']
    SIPUA.publishCallFwd(enabled, fc.forward)
  },

  publishFollowme: function ({ rootGetters }, enabled) {
    const fc = rootGetters['settings/featureCodes']
    SIPUA.publishFollowme(enabled, fc.followme)
  },

  recToggle: function ({ commit }, { callid, enabled }) {
    commit('TOGGLE_SESS_REC', { callid, enabled })
  }
}

const mutations = {
  SET_CONFIG (state, { conf, sipuaEnable }) {
    const clid = conf?.clid.split('|')
    state.uacfg.uri = conf.uri
    state.uacfg.proxy = conf.proxy
    state.uacfg.realm = conf.realm
    state.uacfg.ha1 = conf.ha1
    state.uacfg.country = conf.country_code || 'CA'
    state.clidName = clid[0]
    state.clidNum = clid[1]
    state.maxCalls = conf.panel_call_limit || DEFAULT_MAX_CALLS
    state.vmnum = conf.vm_inbox

    state.audio = new Audio()

    state.volume = LocalStorage.sipuaVolume()

    // send AMI commands that requires realm
    RMQClient.sendCmd({
      action: 'ParkedCalls',
      parkinglot: state.uacfg.realm,
      amictx: 'pbx'
    })

    if (!sipuaEnable) {
      dbg('WARINIG: no softphone read permission. Skip configuring SIPUA')
      return
    }

    try {
      SIPUA.init(this, {
        ...state.uacfg,
        clidName: state.clidName,
        uaVer: this._vm?.$APPVERSION
      })
      state.exten = SIPUA.username
    } catch (err) {
      events.pubError(`SIPUA Configuration error: ${err.message}`)
    }
  },

  SET_REALM_ID (_, realmId) {
    if (!realmId) {
      dbg('Failed to set realm id for SIPUA')
      return
    }
    SIPUA.setRealmID(realmId)
  },

  SET_BROWSER_MEDIA (state, media) {
    state.browserMedia.loading = false
    state.browserMedia.rbEnabled = media.isDevRingerOverrideEnabled
    state.browserMedia.input = media.getInputDevs()
    state.browserMedia.output = media.getOutputDevs()
    state.browserMedia.devInput = media.inputDevice
    state.browserMedia.devOutput = media.outputDevice
    state.browserMedia.devRinger = media.ringerDevice
  },

  NEW_RTC_SESSION (state, event) {
    const callid = event.request.call_id
    snd.stopRinging()
    snd.stopCallProgress()
    try {
      const devIn = LocalStorage.matchInputDevice(state.browserMedia.input)
      const devOut = LocalStorage.matchOutputDevice(state.browserMedia.output)

      if (BrowserMedia.HasSupport()) {
        state.audio.setSinkId(devOut.id)
      }

      dbg(`Media Output device set to '${devOut.label}', id: ${devOut.id}`)
      dbg(`Media Input device set to '${devIn.label}', id: ${devIn.id}`)

      state.audio.srcObject = SIPUA.getMedia(callid, devIn.id)
      state.audio.volume = state.volume
      state.audio.play().catch((e) => dbg('NEW_RTC_SESSION play error: %o', e))

      Session.setAnswered(state.sessions, callid)

      Session.setQoS(event?.session?._connection)

      NotifyClear(callid)

      state.calling = false
      // hold all other session but the new one
      if (Session.hold(state.sessions, callid, false)) {
        SIPUA.setOnHold(callid, false)
      }
    } catch (e) {
      events.pubError('Failed to set media. See debug for more info')
      dbg('Failed to set media: %o', e)
    }
  },

  CALL_INIT (state, data) {
    if (data.recvCall) {
      // incoming calls may be configured to use different output device
      const devOut = LocalStorage.matchRingerDevice(
        state.browserMedia.output
      )
      dbg(`Ringing incoming call on output device '${devOut.label}'`)
      snd.startRinging(devOut.id, state.sessions.length > 0)
    }
    const sess = new Session(data)
    state.calling = false
    state.sessions.unshift(sess)
  },

  CALL_ENDED (state, e) {
    if (!e.message) {
      return
    }
    const callid = e.message.call_id
    Session.delete(state.sessions, callid)
    SIPUA.sessDelete(callid)
    NotifyClear(callid)
  },

  CALL_FAILED (state, e) {
    snd.stopCallProgress()
    snd.stopRinging()
    if (!e.message && e.cause) {
      // ignore early call reject or cancel
      dbg(`Call rejected with cause: ${e.cause}`)
      return
    }
    state.calling = false
    const sess = state.sessions.find((s) => s.callid === e.message.call_id)
    if (sess) {
      NotifyClear(sess.callid)
      Session.delete(state.sessions, sess.callid)
      SIPUA.sessDelete(sess.callid)
      // TODO: not quite clean way to call dispatch from mutation
      this.dispatch(
        'callsLogMissed',
        { num: sess.number, name: sess.name },
        {
          root: true
        }
      )
    }
  },

  ON_REINVITE (state, evt) {
    const req = evt?.request
    const pai = SIPRequestPAI(req)
    if (!pai) {
      dbg('on re-invite events missing PAI header. Skip CID update')
      return
    }

    if (SIPUA.username === pai.uri.user) {
      dbg('Do not update from PAI as it match current user')
      return
    }

    const sess = state.sessions.find((s) => s.callid === req.call_id)
    if (!sess) return
    dbg(
      `Update caller id for running call to "${pai.display_name}" <${pai.uri.user}>`
    )
    sess.name = pai.display_name
    sess.number = pai.uri.user
  },

  SET_CALLING (state, value) {
    if (value) {
      // outgoing calls will play call progress to default output device
      const devOut = LocalStorage.matchOutputDevice(state.browserMedia.output)
      dbg(`Play call progress on output device '${devOut.label}'`)
      snd.startCallProgress(devOut.id)
    }
    state.calling = value
  },

  SET_STATUS (state, status) {
    state.status = status
  },

  SET_INPUT_DEVICE (state, dev) {
    state.browserMedia.devInput = dev
  },

  SET_OUTPUT_DEVICE (state, dev) {
    state.browserMedia.devOutput = dev
  },

  SET_RINGER_DEVICE (state, dev) {
    state.browserMedia.devRinger = dev
  },

  SET_RINGDEV_ENABLED (state, enabled) {
    LocalStorage.setRingerDevEnabled(enabled)
    state.browserMedia.rbEnabled = enabled
  },

  SET_PHONE_DISPLAY (state, display) {
    state.phoneShow = display
  },

  SET_PHONE_DIAL_NUMBER (state, dialNumber) {
    state.phoneDialNumber = dialNumber ?? ''
  },

  SET_VMNUM (state, vmnum) {
    state.vmnum = vmnum
  },

  SET_DND (_, { enabled, code }) {
    SIPUA.publishDND(enabled, code)
  },

  SET_SESS_REC (state, data) {
    if (Session.setRecording(state.sessions, data)) {
      dbg('Call session set monitor/recording ON')
      return
    }
    dbg('Failed to update session and set call recording ON. Not found')
  },

  SET_SESS_CHAN (state, data) {
    if (Session.setChanID(state.sessions, data)) {
      dbg('Call session updated with Asteirsk channel')
      return
    }
    dbg('Failed to update session with Asterisk channel: %o', data)
  },

  SET_CHANINFO (state, data) {
    if (Session.setBridgePeerChan(state.sessions, data)) {
      dbg('Call session updated with Asteirsk bridge peer channel')
      return
    }
    dbg('Failed to update session with Asterisk bridge peer channel: %o', data)
  },

  TOGGLE_SESS_REC (state, { callid, enabled }) {
    Session.toggleRec(state.sessions, callid, enabled)
  },

  SET_AUDIO_VOLUME (state, val) {
    state.volume = ~~val / 100
    state.audio.volume = state.volume
    LocalStorage.setSipuaVolume(state.volume)
  },

  CALL_HOLD (state, { callid, enabled }) {
    try {
      if (Session.hold(state.sessions, callid, enabled)) {
        SIPUA.setOnHold(callid, enabled)
      }
    } catch (err) {
      dbg('Failed to put on hold: %o', err)
    }
    // if hold released, set this RTPSession active audio
    if (!enabled) {
      try {
        state.audio.pause()

        const devIn = LocalStorage.matchInputDevice(state.browserMedia.input)
        const devOut = LocalStorage.matchOutputDevice(
          state.browserMedia.output
        )
        if (BrowserMedia.HasSupport()) {
          state.audio.setSinkId(devOut.id)
        }
        dbg(`Media Output device set to '${devOut.label}', id: ${devOut.id}`)
        dbg(`Media Input device set to '${devIn.label}', id: ${devIn.id}`)

        state.audio.srcObject = SIPUA.getMedia(callid, devIn.id)
        state.audio.volume = state.volume
        state.audio.play().catch((e) => dbg('CALL_HOLD play error: %o', e))
      } catch (err) {
        dbg('Failed to play: %o', err)
      }
    }
  },

  CALL_MUTE (state, { callid, enabled }) {
    dbg(`mute by callid: ${callid}, set enabled: ${enabled}`)
    Session.mute(state.sessions, callid, enabled)
    SIPUA.setMute(callid, enabled)
  },

  CALL_SET_BARGEIN (state, { callid, num, legnum, mode }) {
    Session.setBargein(state.sessions, callid, num, legnum, mode)
  },

  SESSION_DELETE (state, callid) {
    SIPUA.callHangup(callid)
    Session.delete(state.sessions, callid)
  },

  XFER_ACCEPTED_NOTIFY (state, num) {
    let opts = {
      text: 'sipua.xfer.success',
      args: { num: num },
      status: 'success'
    }

    if (num === state.parkExten) {
      opts = { ...opts, ...{ text: 'sipua.xfer.park' } }
    }

    events.pubNotify(opts)
  },

  UPDATE_PARK_PICKUP_CALL (state, { cid, name, num }) {
    const sess = state.sessions.find((s) => s.callid === cid)
    if (!sess) return
    dbg('Update caller id for parked call pick by user', cid)
    sess.name = name
    sess.number = num
  }
}

const state = {
  uacfg: {
    country: 'CA', // default is Canada
    proxy: null,
    realm: null,
    uri: null,
    ha1: null
  },
  exten: null,
  vmnum: 0,
  parkExten: '700',
  maxCalls: 0,
  status: 0,
  clidName: null,
  clidNum: null,
  phoneShow: false,
  phoneDialNumber: '',
  calling: false,
  audio: null,
  volume: 1,
  sessions: [],
  browserMedia: {
    loading: true,
    input: [],
    output: [],
    rbEnabled: false,
    devInput: null,
    devOutput: null,
    devRinger: null
  }
}

const getters = {
  sessions: () => SIPUA.sessions,
  rtpSessById: () => (callid) => SIPUA.getRTPSession(callid),
  browserMedia: (state) => state.browserMedia,
  calling: (state) => state.calling,
  audioVol: (state) => state.volume * 100,
  audio: (state) => state.audio,
  maxCalls: (state) => state.maxCalls,
  maxCallsHit: (state) => state.sessions.length >= state.maxCalls,
  isOnCall: (state) => state.sessions.length > 0,
  realm: (state) => state.uacfg.realm,
  country: (state) => state.uacfg.country,
  callerId: (state) => `"${state.clidName}" <${state.clidNum}>`,
  cidName: (state) => state.clidName,
  exten: (state) => state.exten
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  SIPUA
}
